import { useEffect, useRef } from "react";
import useOrganizationSwitch from "hooks/useOrganizationSwitch";
import contextStore, { ContextStore } from "stores/contextStore";
import { OrganizationContextStorage } from "utils/organizationContextStorage";

/**
 * Owns the selected organization across the three places it lives: browser storage,
 * the in-memory context store and the server session.
 *
 * One instance in App. The server sync used to live in the breadcrumbs, which render twice
 * (top bar and the kept-mounted mobile drawer), so every load switched the organization twice.
 */
const useOrganizationContext = (isLoggedIn: boolean) => {
  const { organizationId, organizationName, organizationTypeId } = contextStore.useState();
  const { switchOrganization } = useOrganizationSwitch();

  // 1. Browser -> store: restore the remembered organization on boot. Runs before sign-in
  //    so the organization is known by the time the user is in.
  useEffect(() => {
    const stored = OrganizationContextStorage.get();
    if (stored?.organizationId) {
      contextStore.update((s) => {
        s.organizationId = stored.organizationId;
        s.organizationName = stored.organizationName;
        s.organizationTypeId = stored.organizationTypeId;
      });
    }
  }, []);

  // 2. Store -> browser: remember every change.
  useEffect(() => {
    if (!organizationId) {
      return;
    }

    OrganizationContextStorage.set({
      organizationId,
      organizationName,
      organizationTypeId,
    } as ContextStore);
  }, [organizationId, organizationName, organizationTypeId]);

  // 3. Store -> server: align the session once each time an organization appears where there
  //    was none - on boot, after the first accessible organization is picked for a fresh browser,
  //    after a user merge clears and refills the context. Needs a signed-in user. A change between
  //    two organizations is switched by the place that made it (the header picker).
  const sessionNeedsSync = useRef(true);

  useEffect(() => {
    // A new sign-in is a new server session, whatever the browser remembers.
    if (!organizationId || !isLoggedIn) {
      sessionNeedsSync.current = true;
      return;
    }

    if (!sessionNeedsSync.current) {
      return;
    }

    sessionNeedsSync.current = false;
    switchOrganization(organizationId);
  }, [isLoggedIn, organizationId, switchOrganization]);
};

export default useOrganizationContext;
